import React from "react";
import { useAuth } from "@clerk/clerk-react";
import { Redirect, Route, RouteProps, useLocation } from "react-router-dom";
import { buildSignInRedirectPath } from "@/lib/sessionExpired";

interface ProtectedRouteProps extends RouteProps {
  component?: React.ComponentType<any>;
}

/**
 * ProtectedRoute component that requires authentication.
 * Unauthenticated users are redirected to sign-in (with return URL).
 * In-app API 401s still raise the session-expired dialog via `dispatchSessionExpired`.
 */
export default function ProtectedRoute({
  component: Component,
  path,
  ...routeProps
}: ProtectedRouteProps): JSX.Element | null {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const isAuthPage = location.pathname.startsWith("/auth");

  if (isAuthPage || !Component) {
    return null;
  }

  return (
    <Route
      path={path}
      {...routeProps}
      render={(routeRenderProps) => {
        // Wait for Clerk without a dedicated loading UI — the shell paints next.
        if (!isLoaded) {
          return null;
        }
        if (!isSignedIn) {
          const returnPath = location.pathname + location.search + location.hash;
          return <Redirect to={buildSignInRedirectPath(returnPath)} />;
        }
        return <Component {...routeRenderProps} />;
      }}
    />
  );
}
